import { EOrganization, ERef, FirebaseTimestamp } from '.';
import {
  ORDER_CANCELLED,
  ORDER_EDITED,
  ORDER_INVOICE_CREATED,
  ORDER_INVOICE_PAID,
  ORDER_INVOICE_PAID_OUT_OF_PLATFORM,
  ORDER_INVOICE_REFUNDED,
  ORDER_MANUAL_SYNC_REQUEST,
  ORDER_NEWSPAPER_ORDER_AT_DEADLINE,
  ORDER_NEWSPAPER_ORDER_CONFIRMED,
  ORDER_NEWSPAPER_ORDER_DELETED,
  OrderSyncTriggerEvent,
  OrderSyncTriggerEventType
} from './events';
import { IntegrationFormat } from './integrationSettings';
import { NewspaperOrder } from './newspaperOrder';
import { Order } from './order';

export enum SyncAttemptStatus {
  IN_PROGRESS = 100,
  SUCCESS = 200,
  SKIPPED = 300,
  BAD_REQUEST = 400,
  INTERNAL_ERROR = 500,
  EXTERNAL_ERROR = 503
}

export type BaseSyncAttempt = {
  createdAt?: FirebaseTimestamp;
  modifiedAt?: FirebaseTimestamp | null;
  order: ERef<Order>;
  publisher: ERef<EOrganization>;
  newspaperOrders: ERef<NewspaperOrder>[];
  triggerEvent: ERef<OrderSyncTriggerEvent>;
  format: IntegrationFormat;
  status: SyncAttemptStatus;
};

export type PendingSyncAttempt = BaseSyncAttempt & {
  status: SyncAttemptStatus.IN_PROGRESS;
};

export type SyncAttemptFile = { fileName: string; fileStoragePath: string };

type SyncAttemptWithFinalization = BaseSyncAttempt & {
  finalizedAt: FirebaseTimestamp;
  files: SyncAttemptFile[];
};

export type SuccessSyncAttempt = SyncAttemptWithFinalization & {
  status: SyncAttemptStatus.SUCCESS;
  message: string;
};

export type SyncAttemptIssue = {
  reason: string;
};

export type SkippedSyncAttempt = SyncAttemptWithFinalization & {
  status: SyncAttemptStatus.SKIPPED;
  issues: SyncAttemptIssue[];
};

export type SyncAttemptIssueWithInstruction = SyncAttemptIssue & {
  toFix: string;
};

export type BadRequestSyncAttempt = SyncAttemptWithFinalization & {
  status: SyncAttemptStatus.BAD_REQUEST;
  issues: SyncAttemptIssueWithInstruction[];
};

export type SyncAttemptIssueWithError = SyncAttemptIssueWithInstruction & {
  error: string;
};

export type InternalErrorSyncAttempt = SyncAttemptWithFinalization & {
  status: SyncAttemptStatus.INTERNAL_ERROR;
  issues: SyncAttemptIssueWithError[];
};

export type ExternalErrorSyncAttempt = SyncAttemptWithFinalization & {
  status: SyncAttemptStatus.EXTERNAL_ERROR;
  issues: SyncAttemptIssueWithError[];
};

export type FinalizedSyncAttempt =
  | SuccessSyncAttempt
  | SkippedSyncAttempt
  | BadRequestSyncAttempt
  | InternalErrorSyncAttempt
  | ExternalErrorSyncAttempt;

export type SyncAttempt = PendingSyncAttempt | FinalizedSyncAttempt;

export const TriggerEventMap: Record<OrderSyncTriggerEventType, string> = {
  [ORDER_CANCELLED]: 'Sync on Cancellation',
  [ORDER_EDITED]: 'Sync on Edit',
  [ORDER_INVOICE_CREATED]: 'Sync on Invoice Creation',
  [ORDER_INVOICE_PAID]: 'Sync on Invoice Payment',
  [ORDER_INVOICE_PAID_OUT_OF_PLATFORM]: 'Sync when Marked Paid',
  [ORDER_INVOICE_REFUNDED]: 'Sync when Invoice Refunded',
  [ORDER_MANUAL_SYNC_REQUEST]: 'Sync on Manual Request',
  [ORDER_NEWSPAPER_ORDER_AT_DEADLINE]: 'Sync on Deadline',
  [ORDER_NEWSPAPER_ORDER_CONFIRMED]: 'Sync on Confirmation',
  [ORDER_NEWSPAPER_ORDER_DELETED]: 'Sync on Edit'
};
